import { Component, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { NewAccountDialogComponent } from '@components/new-account-dialog/new-account-dialog.component';
import { EditPermissionDialogComponent } from '@components/edit-permissions-dialog/edit-permissions-dialog.component';
import { User } from '@models/user';
import { TranslateService } from '@ngx-translate/core';
import { OpsUserService } from '@service/ops-user.service';
import { OpsUserSocketService } from '@service/ops-user-socket.service';
import { Vessel } from 'icreate/common/models/vessel';
import { lastValueFrom, Subject, take, takeUntil } from 'rxjs';
import _ from 'lodash';
import { MessageDialogService } from '@service/message-dialog.service';
import { ChannelCategoryService } from '@service/channel/channel-category.service';
import { ChannelCategory } from 'icreate/common/models/channel-category';
import { EditSessionTimeoutDialog } from '@components/edit-session-timeout-dialog/edit-session-timeout-dialog.component';
import { VesselService } from '@service/vessel.service';
import { toSignal } from '@angular/core/rxjs-interop';

export type UserAndPasswordVisible = User & {
    passwordVisible: boolean;
    showOptionsDialog: boolean;
};

@Component({
    selector: 'app-ops-account',
    templateUrl: './ops-account.component.html',
    styleUrls: ['./ops-account.component.css'],
    standalone: false,
})
export class OpsAccountComponent implements OnInit {
    @Input() vessel!: Vessel;
    @Input() userCanEdit: boolean;

    @ViewChild('deleteDialog') deleteDialog: ConfirmationWindowComponent;
    @ViewChild('errorDialog') errorDialog: ConfirmationWindowComponent;

    userList: UserAndPasswordVisible[];

    deleteDialogMainLabel: string;
    dialogErrorMessage: string;
    categoryList: ChannelCategory[] = [];

    private destroySubscriptionSubject$ = new Subject<void>();
    sessionTimeout: number;

    constructor(
        private dialog: MatDialog,
        private opsUserService: OpsUserService,
        private translateService: TranslateService,
        private opsUserSocketService: OpsUserSocketService,
        private messageDialogService: MessageDialogService,
        private channelCategoryService: ChannelCategoryService,
        public vesselService: VesselService
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        this.userList?.forEach(opsUser => {
            opsUser.showOptionsDialog = false;
        });
    }

    async ngOnInit() {
        const opsUserData = ((await this.opsUserService.getAll(this.vessel.id)) as any) || [];
        this.categoryList = await this.channelCategoryService.getList(this.vessel.id);

        this.userList = opsUserData.map(userData => {
            return {
                ...userData,
                passwordVisible: false,
                showOptionsDialog: false,
            };
        });
        this.userList.forEach(user => {
            this.sessionTimeout = user.sessionTimeout;
        });

        this.opsUserSocketService
            .opsUserCreated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(newOpsUser => {
                if (newOpsUser.vesselId === this.vessel.id) {
                    this.userList.push({
                        ...newOpsUser,
                        passwordVisible: false,
                        showOptionsDialog: false,
                    });
                    this.showMessageDialog('account-settings.message-dialog.create.message', {
                        name: newOpsUser.name,
                    });
                }
            });

        this.opsUserSocketService;
        this.opsUserSocketService
            .opsPerimssionsUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(({ results, vesselId }) => {
                if (vesselId === this.vessel.id) {
                    this.userList = this.userList.map(user => {
                        const incomingUserCategories = results.filter(item => item.opsUserId === user.id);

                        if (incomingUserCategories.length === 0) {
                            return user;
                        }

                        user.permissions = incomingUserCategories;
                        const hasEditChannel = incomingUserCategories.some(cat => cat.editChannel);
                        const hasControlMimic = incomingUserCategories.some(cat => cat.controlMimic);

                        return {
                            ...user,
                            editChannel: hasEditChannel,
                            controlMimic: hasControlMimic,
                        };
                    });

                    this.showMessageDialog('account-settings.permissions-updates.message');
                }
            });
        this.opsUserSocketService
            .opsUserUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(updatedOpsUser => {
                if (updatedOpsUser.vesselId === this.vessel.id) {
                    let equal = true;
                    this.userList = this.userList.map(opsUser => {
                        if (opsUser.id === updatedOpsUser.id) {
                            if (!_.isEqual(opsUser, updatedOpsUser)) equal = false;
                            return {
                                ...updatedOpsUser,
                                passwordVisible: false,
                                showOptionsDialog: false,
                            };
                        } else {
                            return opsUser;
                        }
                    });
                    if (!equal) {
                        this.showMessageDialog('account-settings.message-dialog.update.message', {
                            name: updatedOpsUser.name,
                        });
                    }
                }
            });

        this.opsUserSocketService
            .opsUserDeleted$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(({ opsUserId }) => {
                let indexToDelete = this.userList.findIndex(opsUser => opsUser.id === opsUserId);
                if (indexToDelete !== -1) {
                    this.showMessageDialog('account-settings.message-dialog.delete.message', {
                        name: this.userList[indexToDelete].name,
                    });
                    this.userList.splice(indexToDelete, 1);
                }
            });

        this.opsUserSocketService
            .opsSessionTimeoutUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(({ timeout, vesselId }) => {
                if (this.vessel.id === vesselId) {
                    this.sessionTimeout = timeout;
                    this.showMessageDialog('account-settings.session-timeout-update.message');
                }
            });
    }

    ngOnDestroy() {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    passwordModeChange(targetUser: UserAndPasswordVisible) {
        this.userList = this.userList.map(user => {
            if (user.id === targetUser.id) {
                return {
                    ...user,
                    passwordVisible: !user.passwordVisible,
                };
            }
            return user;
        });
    }

    editPermissions() {
        this.dialog.open(EditPermissionDialogComponent, {
            width: '1200px',
            disableClose: true,
            autoFocus: false,
            data: {
                vesselId: this.vessel.id,
                categoryList: this.categoryList,
                name: '',
                userList: this.userList,
                userListLength: this.userList?.length || 0,
            },
        });
    }

    createAccount() {
        this.dialog.open(NewAccountDialogComponent, {
            width: '474px',
            disableClose: true,
            data: {
                vessel: this.vessel,
                name: '',
                password: '',
                userList: this.userList,
            },
        });
    }

    editSessionTimeout() {
        const dialogRef = this.dialog.open(EditSessionTimeoutDialog, {
            width: '474px',
            disableClose: true,
            data: {
                sessionTimeout: this.sessionTimeout,
            },
        });

        dialogRef.afterClosed().subscribe(async result => {
            if (result !== undefined && result !== null) {
                await this.opsUserService.setSessionTimeout(result, this.vessel.id);
            }
        });
    }

    optionsIconClick(clickedUser: UserAndPasswordVisible) {
        if (!this.userCanEdit) return;
        this.userList = this.userList.map(opsUser => {
            return {
                ...opsUser,
                showOptionsDialog: opsUser.id === clickedUser.id ? !opsUser.showOptionsDialog : false,
            };
        });
    }

    async editAccount(opsUser) {
        this.dialog.open(NewAccountDialogComponent, {
            width: '474px',
            disableClose: true,
            data: {
                vessel: this.vessel,
                id: opsUser.id,
                name: opsUser.name,
                password: opsUser.password,
                userList: this.userList,
            },
        });
    }

    async deleteAccount(opsUser) {
        try {
            if (opsUser.isAdmin) {
                this.dialogErrorMessage = 'account-settings.delete.admin.message';
                this.errorDialog.setVisible(true);
                await lastValueFrom(this.errorDialog.onOptionSelected.pipe(take(1)));

                return;
            }

            this.deleteDialogMainLabel = this.translateService.instant('account-settings.delete.main.title', {
                name: opsUser.name,
            });

            this.deleteDialog.setVisible(true);
            const result = await lastValueFrom(this.deleteDialog.onOptionSelected.pipe(take(1)));

            if (result === ConfirmationWindowTypes.SAVE) {
                await this.opsUserService.delete(opsUser.id);
            }

            this.deleteDialog.setVisible(false);
        } catch (error) {
            console.log(error);
        }
    }

    async duplicateAccount(opsUser) {
        try {
            if (this.userList?.length >= 10) {
                this.dialogErrorMessage = this.translateService.instant(
                    'account-settings.duplicate.error.limit-exceeded',
                    {
                        max: 10,
                    }
                );
                this.errorDialog.setVisible(true);
                await lastValueFrom(this.errorDialog.onOptionSelected.pipe(take(1)));

                return;
            }

            const copiedOpsUser = { ...opsUser };
            copiedOpsUser.id = '';
            const matchingTitles = this.userList
                .filter(user => user.name.startsWith(`${copiedOpsUser.name}_copy_`))
                .map(user => user.name);

            let copyCount = 1;
            let newTitle = `${copiedOpsUser.name}_copy_${copyCount}`;

            while (matchingTitles.includes(newTitle)) {
                copyCount++;
                newTitle = `${copiedOpsUser.name}_copy_${copyCount}`;
            }

            copiedOpsUser.name = newTitle;

            this.dialog.open(NewAccountDialogComponent, {
                width: '474px',
                disableClose: true,
                data: {
                    vessel: this.vessel,
                    name: copiedOpsUser.name,
                    password: copiedOpsUser.password,
                    permissions: copiedOpsUser.permissions,
                    userList: this.userList,
                    isDuplicate: true,
                },
            });
        } catch (error) {
            console.log(error);
        }
    }

    showMessageDialog(message, params?: Record<string, any>) {
        this.messageDialogService.openMessage(message, params);
    }
}
